import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import alternativeContent from './Conversations/alternativeContent.json';
import applyingContent from './Conversations/applyingContent.json';
import behavioralContent from './Conversations/behavioralContent.json';
import clubsContent from './Conversations/clubsContent.json';
import conferenceContent from './Conversations/conferenceContent.json';
import interviewsContent from './Conversations/interviewsContent.json';
import leetcodeContent from './Conversations/leetcodeContent.json';
import linkedinContent from './Conversations/linkedinContent.json';
import networkingContent from './Conversations/networkingContent.json';
import ocsContent from './Conversations/ocsContent.json';
import phoneContent from './Conversations/phoneContent.json';
import processContent from './Conversations/processContent.json';
import resumeContent from './Conversations/resumeContent.json';
import rolesContent from './Conversations/rolesContent.json';
import startContent from './Conversations/startContent.json';
import technicalContent from './Conversations/technicalContent.json';
import alternativeSummary from './Summaries/alternativeSummary.js';
import applyingSummary from './Summaries/applyingSummary.js';
import behavioralSummary from './Summaries/behavioralSummary.js';
import clubsSummary from './Summaries/clubsSummary.js';
import conferenceSummary from './Summaries/conferenceSummary.js';
import interviewsSummary from './Summaries/interviewsSummary.js';
import leetcodeSummary from './Summaries/leetcodeSummary.js';
import linkedinSummary from './Summaries/linkedinSummary.js';
import networkingSummary from './Summaries/networkingSummary.js';
import ocsSummary from './Summaries/ocsSummary.js';
import phoneSummary from './Summaries/phoneSummary.js';
import processSummary from './Summaries/processSummary.js';
import resumeSummary from './Summaries/resumeSummary.js';
import rolesSummary from './Summaries/rolesSummary.js';
import startSummary from './Summaries/startSummary.js';
import technicalSummary from './Summaries/technicalSummary.js';

import { GlobalContext } from '../../contexts/GlobalContext';

import BouncingImage from '../BouncingImage';
import { InfoTooltip } from '../Tooltip';

import useAchievementTriggers from '../../hooks/useAchievementTriggers';

const contentMapping = {
  start: startContent,
  roles: rolesContent,
  process: processContent,
  applying: applyingContent,
  resume: resumeContent,
  interviews: interviewsContent,
  technical: technicalContent,
  leetcode: leetcodeContent,
  behavioral: behavioralContent,
  alternative: alternativeContent,
  networking: networkingContent,
  linkedin: linkedinContent,
  ocs: ocsContent,
  phone: phoneContent,
  conference: conferenceContent,
  clubs: clubsContent,
};

const completedContentMapping = {
  start: startSummary,
  roles: rolesSummary,
  process: processSummary,
  applying: applyingSummary,
  resume: resumeSummary,
  interviews: interviewsSummary,
  technical: technicalSummary,
  leetcode: leetcodeSummary,
  behavioral: behavioralSummary,
  alternative: alternativeSummary,
  networking: networkingSummary,
  linkedin: linkedinSummary,
  ocs: ocsSummary,
  phone: phoneSummary,
  conference: conferenceSummary,
  clubs: clubsSummary,
};

const LessonInterface = ({ route = 'start' }) => {
  const {
    completedNodes,
    setCompletedNodes,
    userProfile,
    questPlan,
    fetchQuest,
    nodes,
    showToastMessage,
    trialStatus,
  } = useContext(GlobalContext);

  const [messages, setMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const [isLessonCompleted, setIsLessonCompleted] = useState(completedNodes.includes(route));
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const checkAchievements = useAchievementTriggers();

  // // Select the appropriate content based on route
  const currentContent = contentMapping[route] || {}; // Default to an empty object if route is not found
  const currentSummary = completedContentMapping[route] || {};
  const lessonTitle = currentContent.title || 'Default Lesson Title'; // Provide a default title if not found
  const lessonLength = currentContent.length || 0; // Default to 0 if not found
  const lessonSummary = currentContent.summary || ''; // Default to empty string if not found

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom whenever messages change
  useEffect(() => {
    if (isStarted) {
      scrollToBottom();
    }
  }, [messages]);

  const typeMessage = async (text, sender) => {
    if (!text) return ''; // Return empty string if no text
    setIsTyping(true);
    let currentText = '';
    for (let char of text) {
      currentText += char;
      setCurrentText(currentText);
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          sender: sender,
          content: currentText,
          isTyping: true,
        },
      ]);
      await new Promise((resolve) => setTimeout(resolve, 5));
    }
    setIsTyping(false);
    setCurrentText('');
    return text;
  };

  const handleOptionSelect = async (optionIndex) => {
    if (!currentQuestion) return;

    setSelectedOption(optionIndex);
    setShowOptions(false);

    // Add the user's selected option
    const userMessage = {
      sender: 'user',
      content: `${currentQuestion.options[optionIndex]}`,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Add empty message for cat's response
    setMessages((prev) => [
      ...prev,
      {
        sender: 'cat',
        content: '',
        isTyping: true,
      },
    ]);

    // Type out the response
    await typeMessage(currentQuestion.responses[optionIndex], 'cat');
    setMessages((prev) => [
      ...prev.slice(0, -1),
      {
        sender: 'cat',
        content: currentQuestion.responses[optionIndex],
      },
    ]);

    setCurrentQuestion(null);
    setSelectedOption(null); // Reset selected option
    setCurrentIndex((prev) => prev + 1);
  };

  const handleLessonComplete = async () => {
    console.log('Lesson completion initiated for route:', route);
    if (questPlan?.quest_id) {
      console.log('Quest ID found:', questPlan.quest_id);
      try {
        // Get the lesson title from the progress tree nodes
        const lessonTitle = nodes[route]?.title;
        console.log('Looking for lesson:', lessonTitle);

        // Search through all weeks for the lesson task
        for (let weekIndex = 0; weekIndex < questPlan.quest_plan.length; weekIndex++) {
          const week = questPlan.quest_plan[weekIndex];
          const lessonTask = week.tasks.lessons.find((task) => task.lesson_name === lessonTitle);
          console.log('Lesson task found:', lessonTask);

          if (lessonTask) {
            // Update the task completion status
            await axios.put(`/api/v1/quest/${questPlan.quest_id}/task`, {
              task_type: 'lessons',
              task_name: lessonTask.lesson_name,
              completed: true,
              week_index: weekIndex,
              task_index: week.tasks.lessons.indexOf(lessonTask),
            });

            // Refresh quest data
            fetchQuest();

            // Show toast message that says this lesson is completed
            showToastMessage(`Completing "${lessonTitle}" has been added to your quest!`);
            break; // Exit the loop once we've found and updated the task
          }
        }
      } catch (error) {
        console.error('Error updating quest task:', error);
      }
    }

    if (!completedNodes.includes(route)) {
      const updatedNodes = [...completedNodes, route];
      setCompletedNodes(updatedNodes);

      // Update in the database as well
      await axios.put(`/api/v1/user/${userProfile.user_id}/completed-lessons`, {
        completed_lessons: updatedNodes,
      });

      // NEW: Trigger task generation based on lesson completion
      try {
        await axios.post(`/api/v1/user/${userProfile.user_id}/lesson-completed`, {
          lesson_id: route,
          completed_at: new Date().toISOString(),
        });
        console.log(`New personalized tasks generated for completing: ${route}`);
      } catch (error) {
        console.error('Error triggering task generation:', error);
      }

      if (updatedNodes.length >= 5) {
        checkAchievements.checkLessonsCompleted();
      }

      // Check for new NavLink to unlock
      let newNavLink = '';
      if (route === 'roles') newNavLink = 'Job Board';
      else if (route === 'applying') newNavLink = 'Application Tracking';
      else if (route === 'leetcode') newNavLink = 'Technical Prep';
      else if (route === 'behavioral') newNavLink = 'Behavioral Prep';
      else if (route === 'networking')
        newNavLink = trialStatus?.has_trial ? 'Networking' : 'Alumni Finder';
      else if (route === 'start') newNavLink = 'Home';

      const messagesForTutorialModal = {
        'Job Board': 'We will show you where to find job postings to apply to!',
        'Application Tracking':
          'InternHub can now automatically keep track of all your applications for you!',
        Home: 'See your streak, badges, and more fun stuff!',
        'Technical Prep': 'Learn Leetcode and do mock interviews directly on InternHub!',
        'Behavioral Prep': 'Prepare a behavioral interview cheat-sheet right on InternHub!',
        'Alumni Finder': 'Search for alumni from your school to chat with or get referrals!',
        Networking: 'Search for people from your school to chat with or get referrals!',
      };

      const routeMapping = {
        'Technical Prep': '/technical',
        'Behavioral Prep': '/behavioral-interview',
        'Job Board': '/job-board',
        'Application Tracking': '/application-tracking',
        'Alumni Finder': '/alumni-finder',
        Networking: '/networking',
        Home: '/home',
      };

      if (newNavLink) {
        console.log(`New feature unlocked: ${newNavLink}`);
        navigate('/progress-tree', {
          state: {
            alert: {
              visible: true,
              title: newNavLink,
              description: messagesForTutorialModal[newNavLink],
              link: routeMapping[newNavLink],
            },
          },
        });
        return;
      }
    } else {
      console.log('No new features unlocked, lesson already completed.');
    }

    navigate('/progress-tree');
  };

  const addNextMessage = async () => {
    if (currentIndex < currentContent.baseConversation.length) {
      const message = currentContent.baseConversation[currentIndex];

      // Check if the current node is completed
      if (completedNodes.includes(route)) {
        // If completed, display the entire conversation
        setMessages(currentContent.baseConversation);
        setCurrentIndex(currentContent.baseConversation.length); // Set index to the end
        return;
      }

      // Check if this message is a question
      const questionData = currentContent.questions.find((q) => q.index === currentIndex);

      if (questionData) {
        // Show the question message
        setMessages((prev) => [...prev, message]);
        if (message.content) {
          await typeMessage(message.content, message.sender);
          setMessages((prev) => [...prev.slice(0, -1), { ...message, isTyping: false }]);
        }

        setCurrentQuestion(questionData);
        setShowOptions(true);
        return;
      }

      // Add an initial empty message to show the typing animation
      setMessages((prev) => [
        ...prev,
        {
          ...message,
          content: message.content || '',
          isTyping: true,
        },
      ]);

      if (message.content) {
        await typeMessage(message.content, message.sender);
      }
      setMessages((prev) => [
        ...prev.slice(0, -1),
        {
          ...message,
          content: message.content || '',
          isTyping: false,
        },
      ]);

      setCurrentIndex((prev) => prev + 1);
    }
  };

  // Add new handler for advancing the conversation
  const handleAdvanceConversation = () => {
    if (!isTyping && !showOptions && isStarted) {
      currentIndex >= currentContent.baseConversation.length
        ? handleLessonComplete()
        : addNextMessage();
    }
  };

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        handleAdvanceConversation();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [currentIndex, isTyping, showOptions, isStarted]); // Add dependencies

  // If the lesson is completed, display the summary content
  if (isLessonCompleted) {
    return (
      <div className="h-screen flex items-start justify-center bg-gray-50 pt-10">
        <div className="max-w-4xl mx-4">
          <h1 className="text-4xl font-bold mb-4 text-center">{lessonTitle}</h1>
          {currentSummary}
          <div className="flex justify-center">
            <button
              onClick={() => {
                setIsLessonCompleted(false); // Reset the lesson completion state
                // navigate(0); // Rerender the page by navigating to the current page
              }}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg"
            >
              Restart Interactive Lesson
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex items-start justify-center bg-gray-50 pt-10">
      <div className="max-w-3xl w-full mx-40">
        <h1 className="text-4xl font-bold mb-4 text-center">{lessonTitle}</h1>
        <div className="flex flex-col bg-white border shadow-xs rounded-xl h-[70vh] w-full">
          <div
            className="flex-1 p-4 md:p-5 overflow-y-auto items-center justify-center cursor-pointer w-full"
            onClick={handleAdvanceConversation}
          >
            <div className="space-y-6 flex flex-col h-full w-full max-w-full">
              {!isStarted && (
                <div className="flex h-64 items-center justify-center p-4 h-full mx-auto gap-6">
                  <div className="flex items-center justify-center">
                    <BouncingImage width="10" imageLink="/trainingcar.png" />
                    <p className="text-lg ml-7">
                      Hi there! This lesson will take about <strong>{lessonLength}</strong>. You'll
                      learn about:
                      <br />
                      {lessonSummary.map((summary, index) => (
                        <span key={index}>
                          {index + 1}. {summary} <br />
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-3 ${
                    message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'
                  }`}
                >
                  <div className="shrink-0 h-10 w-10 rounded-[20%] overflow-hidden border-2 bg-gray-100 border-white shadow-lg">
                    <img
                      src={
                        message.sender === 'user'
                          ? '/jane.png' // Replace with actual user avatar
                          : '/writingcar.png' // Replace with actual cat avatar
                      }
                      alt={message.sender === 'user' ? 'User avatar' : 'Cat avatar'}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div
                    className={`${message.image && !message.content ? 'w-auto max-w-[75%]' : 'w-[75%]'}`}
                  >
                    <div
                      className={`rounded-xl p-4 ${
                        message.sender === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100'
                      }`}
                    >
                      {message.sender === 'cat' && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold">InternHub Cat</span>
                        </div>
                      )}
                      {message.content && (
                        <p className="whitespace-pre-line min-h-6">{message.content}</p>
                      )}
                      {/* Render image if present */}
                      {message.image && (
                        <div className="mt-3">
                          <img
                            src={message.image}
                            alt="Tom Lam"
                            className="rounded-lg max-w-full max-h-[400px] object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {showOptions && currentQuestion && (
                <div className="flex flex-col gap-2 w-full">
                  {currentQuestion.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(index)}
                      className={`text-left p-4 w-full transition-all ${
                        selectedOption === index
                          ? 'bg-blue-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      } rounded-xl`}
                      disabled={selectedOption !== null}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="flex justify-between p-4 bg-white border-t mt-4">
            {/* Skip Lesson Button */}
            <div className="relative">
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg shadow-md transition-colors"
                onClick={handleLessonComplete}
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => setIsTooltipVisible(false)}
              >
                Skip Lesson
              </button>
              {isTooltipVisible && (
                <div className="absolute opacity-100 transition-opacity duration-200 z-10 whitespace-nowrap px-3 py-2 bg-gray-800 text-white text-sm rounded-md -top-16 left-1/2 transform -translate-x-1/2">
                  Feel free to skip the lesson if you feel like you already know the material!
                  <br />
                  You can always come back to it later 😺
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setIsStarted(true);
                currentIndex >= currentContent.baseConversation.length
                  ? handleLessonComplete()
                  : addNextMessage();
              }}
              disabled={isTyping || showOptions}
              className="w-full sm:w-auto py-3 px-4 inline-flex justify-center items-center gap-x-2 text-md font-bold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 shadow-md disabled:pointer-events-none"
            >
              {currentIndex === 0
                ? 'Start Lesson'
                : currentIndex >= currentContent.baseConversation.length
                  ? 'Complete Lesson'
                  : 'Continue'}
            </button>
          </div>
        </div>
      </div>
      <InfoTooltip>
        <p>Click the "Start Lesson" button to get started.</p>
        <br />
        <p>
          Sometimes there are interactions where you can select an answer, so make sure to pay
          attention and click those.
        </p>
      </InfoTooltip>
    </div>
  );
};

export default LessonInterface;
